const activePollIntervalMs = 5000;
// after a sidebar update (assignee/reviewer change) the session row is created asynchronously,
// so poll a few extra times before falling back to the idle interval
const rapidPollIntervalMs = 1500;
// keep a slow poll when nothing runs, a rerun started from the Actions page must show up here too
const idlePollIntervalMs = 30000;

const activeSessionPhases = new Set(['queued', 'working', 'applying']);
let didSchedulePageReload = false;

function timelineItemUnix(el: Element): number {
  const timeEl = el.querySelector<HTMLElement>('relative-time[datetime]');
  if (!timeEl) return 0;
  const parsed = Date.parse(timeEl.getAttribute('datetime')!);
  return Number.isNaN(parsed) ? 0 : Math.floor(parsed / 1000);
}

// placeSessionCard inserts a session entry at its chronological position, agent runs must not pile
// up at the bottom of the timeline.
function placeSessionCard(card: HTMLElement): void {
  const list = document.querySelector<HTMLElement>('.issue-content-left .comment-list') ?? document.querySelector<HTMLElement>('.comment-list');
  if (!list) return;
  const cardUnix = Number(card.getAttribute('data-session-created') || '0');
  const endMarker = document.querySelector<HTMLElement>('#timeline-comments-end');
  for (const item of list.children) {
    if (item === card || item === endMarker) continue;
    if (!item.classList.contains('timeline-item')) continue;
    if (item.classList.contains('form')) continue;
    const itemUnix = timelineItemUnix(item);
    if (itemUnix && cardUnix && itemUnix > cardUnix) {
      list.insertBefore(card, item);
      return;
    }
  }
  if (endMarker?.parentElement === list) {
    list.insertBefore(card, endMarker);
  } else {
    list.append(card);
  }
}

type ReviewerBadge = {phase: string, label: string};

function syncReviewerBadges(cards: HTMLElement[]): void {
  const badgeByUser = new Map<string, ReviewerBadge>();
  for (const card of cards) {
    const user = card.getAttribute('data-agent-user');
    if (!user) continue;
    // the newest session of an agent wins, the cards are ordered oldest first
    badgeByUser.set(user.toLowerCase(), {
      phase: card.getAttribute('data-session-phase') || '',
      label: card.getAttribute('data-phase-label') || '',
    });
  }
  for (const badge of document.querySelectorAll<HTMLElement>('.ee-agent-reviewer-badge')) {
    badge.remove();
  }
  const reviewerList = document.querySelector<HTMLElement>('.issue-content-right .issue-sidebar-combo');
  if (!reviewerList) return;
  for (const link of reviewerList.querySelectorAll<HTMLAnchorElement>('.ui.list .item a[href]')) {
    const user = link.getAttribute('href')!.split('/').filter(Boolean).pop()?.toLowerCase();
    if (!user) continue;
    const info = badgeByUser.get(user);
    if (!info) continue;
    const badge = document.createElement('span');
    badge.className = `ee-agent-reviewer-badge is-${info.phase}`;
    badge.textContent = info.label;
    link.after(badge);
  }
}

function collectActiveSessionIDs(): Set<string> {
  const active = new Set<string>();
  for (const card of document.querySelectorAll<HTMLElement>('.ee-agent-session[data-session-id]')) {
    const phase = card.getAttribute('data-session-phase') || '';
    if (activeSessionPhases.has(phase)) {
      active.add(card.getAttribute('data-session-id')!);
    }
  }
  return active;
}

// a finished session usually produced comments, reviews or sub issues that are only rendered by the
// server, so the timeline has to be reloaded once. A draft in an editor is never thrown away.
function hasUnsavedEditorContent(): boolean {
  for (const textarea of document.querySelectorAll<HTMLTextAreaElement>('.page-content.repository textarea')) {
    if (textarea.value.trim()) return true;
  }
  return false;
}

function shouldReloadPageAfterCompletion(activeBefore: Set<string>): boolean {
  if (didSchedulePageReload) return false;
  const path = window.location.pathname;
  if (!path.includes('/pulls/') && !path.includes('/issues/')) return false;
  if (hasUnsavedEditorContent()) return false;
  for (const card of document.querySelectorAll<HTMLElement>('.ee-agent-session[data-session-id]')) {
    if (card.getAttribute('data-session-phase') !== 'completed') continue;
    if (activeBefore.has(card.getAttribute('data-session-id')!)) return true;
  }
  return false;
}

// syncCards replaces, inserts and removes the timeline entries identified by one attribute, so the
// session cards and the pending request entries share the same update logic.
function syncCards(fresh: HTMLElement, attribute: string): HTMLElement[] {
  const cards: HTMLElement[] = [];
  const seen = new Set<string>();
  for (const freshCard of fresh.querySelectorAll<HTMLElement>(`.ee-agent-session[${CSS.escape(attribute)}]`)) {
    const key = freshCard.getAttribute(attribute)!;
    seen.add(key);
    const existing = document.querySelector<HTMLElement>(`.ee-agent-session[${CSS.escape(attribute)}="${CSS.escape(key)}"]`);
    if (existing) {
      existing.replaceWith(freshCard);
    } else {
      placeSessionCard(freshCard);
    }
    cards.push(freshCard);
  }
  for (const stale of document.querySelectorAll<HTMLElement>(`.ee-agent-session[${CSS.escape(attribute)}]`)) {
    if (!seen.has(stale.getAttribute(attribute)!)) stale.remove();
  }
  return cards;
}

async function loadAgentSessions(container: HTMLElement): Promise<void> {
  const url = container.getAttribute('data-agent-sessions-url');
  if (!url) return;
  const response = await fetch(url, {headers: {'x-requested-with': 'XMLHttpRequest'}}); // eslint-disable-line no-restricted-globals
  if (!response.ok) return;
  const activeBefore = collectActiveSessionIDs();
  const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
  const fresh = parsed.querySelector<HTMLElement>('#ee-agent-sessions');
  if (!fresh) return;
  container.setAttribute('data-agent-sessions-active', fresh.getAttribute('data-agent-sessions-active') || 'false');

  const cards = syncCards(fresh, 'data-session-id');
  // requests that wait for the running session are their own entries, they disappear as soon as
  // the deferred run starts
  syncCards(fresh, 'data-pending-id');
  syncReviewerBadges(cards);
  if (shouldReloadPageAfterCompletion(activeBefore)) {
    didSchedulePageReload = true;
    window.setTimeout(() => window.location.reload(), 1200);
  }
}

// initAgentSessions renders the agent session entries of an issue/pull page in chronological order
// and keeps polling while at least one session is still queued or running.
export function initAgentSessions(): void {
  const container = document.querySelector<HTMLElement>('#ee-agent-sessions');
  if (!container) return;

  let pollTimer: ReturnType<typeof setTimeout> | null = null;
  let rapidPolls = 0;

  async function poll() {
    // browsers throttle background tabs anyway, skipping hidden tabs keeps the server quiet
    if (document.visibilityState === 'visible') {
      try {
        await loadAgentSessions(container!);
      } catch {
        // keep polling, a transient network error must not stop the live updates
      }
    }
    if (!document.contains(container)) return;
    scheduleNextPoll();
  }

  function scheduleNextPoll() {
    if (pollTimer) clearTimeout(pollTimer);
    const isActive = container!.getAttribute('data-agent-sessions-active') === 'true';
    const delay = isActive ? activePollIntervalMs : rapidPolls > 0 ? rapidPollIntervalMs : idlePollIntervalMs;
    if (rapidPolls > 0) rapidPolls--;
    pollTimer = setTimeout(poll, delay);
  }

  function kickPoll() {
    rapidPolls = 5;
    if (pollTimer) clearTimeout(pollTimer);
    poll();
  }

  // issue sidebar updates are applied in place, so the initial page init is not called again.
  // Watch the timeline for the assignment/review-request event and refresh the session card immediately.
  const timelineEnd = document.querySelector<HTMLElement>('#timeline-comments-end');
  const timelineList = timelineEnd?.parentElement;
  if (timelineList) {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node instanceof Element && node.classList.contains('timeline-item') && !node.classList.contains('ee-agent-session')) {
            kickPoll();
            return;
          }
        }
      }
    });
    observer.observe(timelineList, {childList: true});
  }

  poll();
}
